import {
  Alert,
  Divider,
  FormControl,
  HairlineCard,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  StatusChip,
  TextField,
  Typography,
  FormHelperText,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  CommitteeDecision,
  FileDetail,
  StaffReportStatus,
  Step6Data,
} from '@munifocus/contracts-gov';
import { EscalationStepLayout } from '../../components/EscalationStepLayout';
import { useSaveStage } from '../../lib/files-api';
import { useAppeals } from '../../lib/appeals-api';
import { useSubmitValidation } from '../../lib/useSubmitValidation';
import { KINGS_BENCH_WINDOW_DAYS, statutoryClock } from '../../lib/escalation-display';

type AppealAnswer = 'yes' | 'no' | '';

/**
 * Type 3 sub-flow, Step 6 — Appeal Committee (ticket E3). Opens with the 4.9
 * appeal gate: if no appeal was filed inside the 14-day window the notice is
 * final and binding and the file moves to Step 7. If an appeal was filed, the
 * committee's decision is recorded; `rescind` closes the file, while confirm /
 * modify / extend proceed to Step 7 once the 14-day King's Bench window runs.
 */
export function Step6Committee({ file }: { file: FileDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveStage(file.id, 'step_6');
  // Where the appeals module is on, appeals and the committee's decision are
  // recorded on the file's Appeals panel, which writes this step itself.
  // Not asked where the file's detail already says the module is off here.
  const appealsModule = Boolean(
    useAppeals(file.modules?.appeals === false ? '' : file.id).data?.enabled,
  );
  const initial = (file.stageData?.step_6 ?? {}) as Step6Data;

  const [appealFiled, setAppealFiled] = useState<AppealAnswer>(
    initial.appealFiled === undefined ? '' : initial.appealFiled ? 'yes' : 'no',
  );
  const [staffReportStatus, setStaffReportStatus] = useState<StaffReportStatus>(
    initial.staffReportStatus ?? 'pending',
  );
  const [committeeDecision, setCommitteeDecision] = useState<CommitteeDecision | ''>(
    initial.committeeDecision ?? '',
  );
  const [decisionDate, setDecisionDate] = useState(initial.decisionDate?.slice(0, 10) ?? '');

  const appealed = appealFiled === 'yes';
  const rescinded = appealed && committeeDecision === 'rescind';
  const kingsBench =
    appealed && committeeDecision && committeeDecision !== 'rescind' && decisionDate
      ? statutoryClock(decisionDate, KINGS_BENCH_WINDOW_DAYS)
      : null;

  // Need an appeal answer; if appealed, a committee decision is required too.
  const v = useSubmitValidation({
    appealFiled: appealFiled !== '' ? null : t('common.validation.choose'),
    committeeDecision: !appealed || committeeDecision !== '' ? null : t('common.validation.choose'),
  });

  const onSave = () =>
    save.mutate(
      {
        appealFiled: appealed,
        staffReportStatus: appealed ? staffReportStatus : undefined,
        committeeDecision: appealed ? committeeDecision || undefined : undefined,
        decisionDate: appealed ? decisionDate || undefined : undefined,
        // A rescind decision is terminal — advance escalation -> closed.
        advance: rescinded,
      },
      { onSuccess: () => navigate(`/files/${file.id}`) },
    );

  return (
    <EscalationStepLayout
      file={file}
      step="step_6"
      onSave={v.guard(onSave)}
      saving={save.isPending}
      saveLabel={rescinded ? t('gov.escalation.step6.closeFile') : t('common.actions.save')}
    >
      {save.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}
      {appealsModule && <Alert severity="info">{t('gov.escalation.step6.appealsModule')}</Alert>}

      {/* 4.9 appeal gate. */}
      <HairlineCard sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{t('gov.escalation.step6.appealGate')}</Typography>
          <FormControl fullWidth size="small" error={Boolean(v.errors.appealFiled)}>
            <InputLabel>{t('gov.escalation.step6.appealFiled')}</InputLabel>
            <Select
              label={t('gov.escalation.step6.appealFiled')}
              aria-label={t('gov.escalation.step6.appealFiled')}
              inputRef={v.register('appealFiled')}
              value={appealFiled}
              onChange={(e) => setAppealFiled(e.target.value as AppealAnswer)}
            >
              <MenuItem value="yes">{t('gov.escalation.step6.appealYes')}</MenuItem>
              <MenuItem value="no">{t('gov.escalation.step6.appealNo')}</MenuItem>
            </Select>
            {v.errors.appealFiled && <FormHelperText>{v.errors.appealFiled}</FormHelperText>}
          </FormControl>
        </Stack>
      </HairlineCard>

      {appealFiled === 'no' && (
        <Alert severity="info">{t('gov.escalation.step6.finalBinding')}</Alert>
      )}

      {appealed && (
        <>
          <FormControl fullWidth size="small">
            <InputLabel>{t('gov.escalation.step6.staffReport')}</InputLabel>
            <Select
              label={t('gov.escalation.step6.staffReport')}
              aria-label={t('gov.escalation.step6.staffReport')}
              value={staffReportStatus}
              onChange={(e) => setStaffReportStatus(e.target.value as StaffReportStatus)}
            >
              <MenuItem value="pending">{t('gov.escalation.step6.reportPending')}</MenuItem>
              <MenuItem value="prepared">{t('gov.escalation.step6.reportPrepared')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" error={Boolean(v.errors.committeeDecision)}>
            <InputLabel>{t('gov.escalation.step6.decision')}</InputLabel>
            <Select
              label={t('gov.escalation.step6.decision')}
              aria-label={t('gov.escalation.step6.decision')}
              inputRef={v.register('committeeDecision')}
              value={committeeDecision}
              onChange={(e) => setCommitteeDecision(e.target.value as CommitteeDecision)}
            >
              <MenuItem value="confirm">{t('gov.escalation.step6.confirm')}</MenuItem>
              <MenuItem value="modify">{t('gov.escalation.step6.modify')}</MenuItem>
              <MenuItem value="extend">{t('gov.escalation.step6.extend')}</MenuItem>
              <MenuItem value="rescind">{t('gov.escalation.step6.rescind')}</MenuItem>
            </Select>
            {v.errors.committeeDecision && (
              <FormHelperText>{v.errors.committeeDecision}</FormHelperText>
            )}
          </FormControl>

          {rescinded ? (
            <Alert severity="warning">{t('gov.escalation.step6.rescindNote')}</Alert>
          ) : (
            committeeDecision && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t('gov.escalation.step6.decisionDate')}
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={decisionDate}
                  onChange={(e) => setDecisionDate(e.target.value)}
                />

                {kingsBench && (
                  <HairlineCard sx={{ p: 2 }}>
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <Stack spacing={0.25}>
                        <Typography variant="overline" color="text.secondary">
                          {t('gov.escalation.step6.kingsBench')}
                        </Typography>
                        <Typography variant="h6">
                          {kingsBench.expired
                            ? t('gov.escalation.step6.kingsBenchClosed')
                            : t('gov.escalation.step6.kingsBenchDaysLeft', {
                                count: kingsBench.daysRemaining,
                              })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('gov.escalation.step5.appealDeadline', {
                            date: kingsBench.deadlineIso,
                          })}
                        </Typography>
                      </Stack>
                      <StatusChip
                        status={kingsBench.expired ? 'success' : 'warning'}
                        label={
                          kingsBench.expired
                            ? t('gov.escalation.step6.proceedStep7')
                            : t('gov.escalation.step5.appealOpen')
                        }
                      />
                    </Stack>
                  </HairlineCard>
                )}

                <Divider />
                <Alert severity="info">{t('gov.escalation.step6.proceedNote')}</Alert>
              </>
            )
          )}
        </>
      )}
    </EscalationStepLayout>
  );
}
